// Optimized Firestore Service v2 - Performance Optimized
// Uses onSnapshot for real-time updates with instant cache loading
// Includes query limits, execution time logging, and indexed field verification
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot, 
  getDoc,
  doc,
  getDocsFromCache
} from 'firebase/firestore';
import { db, isFirebaseInitialized } from './config-v9';

// Collection names
const PRODUCTS_COLLECTION = "products";
const CATEGORIES_COLLECTION = "categories";
const COUPONS_COLLECTION = "coupons";
const ANNOUNCEMENTS_COLLECTION = "announcements";
const FOUNDER_VIDEOS_COLLECTION = "founderVideos";

// Query limits for performance
const MAX_PRODUCTS_LIMIT = 500; // Reasonable limit for product listings
const MAX_CATEGORIES_LIMIT = 100; // More than enough for categories
const MAX_COUPONS_LIMIT = 50; // Reasonable limit for active coupons
const MAX_ANNOUNCEMENTS_LIMIT = 20; // Only show recent announcements
const MAX_VIDEOS_LIMIT = 50; // Reasonable limit for videos

/**
 * Log query execution time for debugging
 */
const logQueryTime = (queryName, startTime, docCount, fromCache = false) => {
  const executionTime = Date.now() - startTime;
  const source = fromCache ? 'cache' : 'server';
  console.log(`⏱️ [${queryName}] Executed in ${executionTime}ms from ${source} (${docCount} docs)`);
  
  // Warn if query takes too long
  if (executionTime > 3000 && !fromCache) {
    console.warn(`⚠️ [${queryName}] Slow query detected: ${executionTime}ms - consider adding indexes`);
  }
};

/**
 * Real-time subscription for products with cache-first loading
 * Uses indexed fields: none (full collection read, but limited)
 * Returns unsubscribe function for cleanup
 */
export const subscribeToProducts = (callback, maxLimit = MAX_PRODUCTS_LIMIT) => {
  console.log('🔍 subscribeToProducts called:', {
    isInitialized: isFirebaseInitialized(),
    hasDb: !!db,
    maxLimit
  });
  
  if (!isFirebaseInitialized() || !db) {
    console.error('❌ Firestore not initialized - cannot fetch products');
    console.error('Firebase state:', {
      isInitialized: isFirebaseInitialized(),
      db: db ? 'exists' : 'null',
      dbType: typeof db
    });
    callback([]);
    return () => {};
  }
  
  const startTime = Date.now();
  const productsRef = collection(db, PRODUCTS_COLLECTION);
  // Add limit to prevent reading entire collection
  const productsQuery = query(productsRef, limit(maxLimit));
  let isFirstCall = true;
  let hasCalledBack = false;
  
  console.log('📡 Setting up products subscription...');
  
  // CRITICAL: Reduced timeout from 300ms to 100ms for faster first paint
  // Timeout to ensure callback is called quickly for new users
  const timeoutId = setTimeout(() => {
    if (!hasCalledBack) {
      console.warn('⚠️ Products subscription timeout - no data received after 100ms');
      // Call with empty array immediately so UI can render
      callback([]);
      hasCalledBack = true;
    }
  }, 100); // Reduced from 300ms to 100ms for faster first paint
  
  // onSnapshot automatically uses cache first, then syncs with server
  // This provides instant loading from cache for returning users
  const unsubscribe = onSnapshot(
    productsQuery,
    (snapshot) => {
      clearTimeout(timeoutId);
      hasCalledBack = true;
      
      const fromCache = snapshot.metadata.fromCache;
      const products = snapshot.docs.map((doc) => {
        const data = doc.data();
        const { id: customId, ...dataWithoutId } = data;
        return { 
          id: doc.id,
          ...dataWithoutId,
          customId: customId || null
        };
      });
      
      if (isFirstCall) {
        logQueryTime('subscribeToProducts', startTime, products.length, fromCache);
        console.log('✅ Products subscription active:', {
          count: products.length,
          fromCache,
          firstProduct: products[0] ? { id: products[0].id, name: products[0].name } : null
        });
        isFirstCall = false;
      }
      
      callback(products);
    },
    (error) => {
      clearTimeout(timeoutId);
      hasCalledBack = true;
      
      console.error('❌ Error in products subscription:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Log all errors for debugging
      if (error.code === 'permission-denied') {
        console.error('❌ PERMISSION DENIED: Check Firestore security rules. Products collection may not be readable.');
      } else if (error.code === 'unavailable') {
        console.error('❌ UNAVAILABLE: Firestore service is unavailable. Check your internet connection.');
      } else if (error.code === 'failed-precondition') {
        console.error('❌ FAILED PRECONDITION: Missing Firestore index. Check console for index creation link.');
      } else {
        console.error('❌ UNEXPECTED ERROR:', error);
      }
      
      callback([]);
    }
  );
  
  return () => {
    clearTimeout(timeoutId);
    unsubscribe();
    console.log('🔌 Products subscription unsubscribed');
  };
};

/**
 * Real-time subscription for products by category
 * Uses indexed fields: category (ensure composite index: category + isActive)
 * Returns unsubscribe function for cleanup
 */
export const subscribeToProductsByCategory = (category, callback, maxLimit = MAX_PRODUCTS_LIMIT) => {
  if (!isFirebaseInitialized() || !db || !category) {
    callback([]);
    return () => {};
  }
  
  const startTime = Date.now();
  const normalizedCategory = String(category).toLowerCase().trim();
  const productsRef = collection(db, PRODUCTS_COLLECTION);
  
  // Query uses indexed field: category
  // Ensure Firestore has composite index: products: category (ASC), isActive (ASC)
  const categoryQuery = query(
    productsRef, 
    where("category", "==", normalizedCategory),
    limit(maxLimit)
  );
  
  let isFirstCall = true;
  let useFallback = false;
  let fallbackUnsubscribe = null;
  
  const unsubscribe = onSnapshot(
    categoryQuery,
    (snapshot) => {
      const fromCache = snapshot.metadata.fromCache;
      const products = snapshot.docs.map((doc) => {
        const data = doc.data();
        const { id: customId, ...dataWithoutId } = data;
        return { 
          id: doc.id,
          ...dataWithoutId,
          customId: customId || null
        };
      });
      
      // OPTIMIZED: If no products found from direct query, try fallback with timeout and reduced limit
      if (products.length === 0 && !useFallback && isFirstCall) {
        console.warn(`⚠️ No products found for category: "${normalizedCategory}". Trying fallback: fetching all products and filtering client-side.`);
        useFallback = true;
        
        // OPTIMIZED: Reduced limit from 3x to 1.5x to improve performance (was fetching 1500, now 750 max)
        // Also add timeout to prevent slow fallback from blocking UI
        const fallbackLimit = Math.min(Math.ceil(maxLimit * 1.5), 750); // Cap at 750 products
        const allProductsQuery = query(productsRef, limit(fallbackLimit));
        
        // OPTIMIZED: Add timeout to fallback - if it takes too long, just return empty array
        const fallbackTimeout = setTimeout(() => {
          if (fallbackUnsubscribe) {
            fallbackUnsubscribe();
            fallbackUnsubscribe = null;
          }
          console.warn(`⚠️ Fallback query timeout for category "${normalizedCategory}"`);
          callback([]);
        }, 2000); // 2 second timeout for fallback
        
        fallbackUnsubscribe = onSnapshot(
          allProductsQuery,
          (allSnapshot) => {
            clearTimeout(fallbackTimeout);
            
            const allProducts = allSnapshot.docs.map((doc) => {
              const data = doc.data();
              const { id: customId, ...dataWithoutId } = data;
              return { 
                id: doc.id,
                ...dataWithoutId,
                customId: customId || null
              };
            });
            
            // OPTIMIZED: More efficient filtering - pre-compute normalized category once
            const normalizedSearchCatNoSpaces = normalizedCategory.replace(/[\s-]/g, '');
            
            // Filter products by category (case-insensitive, handle variations)
            const filtered = allProducts.filter(product => {
              const productCategory = String(product.category || '').toLowerCase().trim();
              // Try exact match first (fastest)
              if (productCategory === normalizedCategory) return true;
              // Try matching without spaces/dashes
              const normalizedProductCat = productCategory.replace(/[\s-]/g, '');
              if (normalizedProductCat === normalizedSearchCatNoSpaces) return true;
              // Try partial match (for cases like "venkatagiri-pattu-saree" vs "venkatagiri")
              if (productCategory.includes(normalizedCategory) || normalizedCategory.includes(productCategory)) {
                return true;
              }
              return false;
            }).slice(0, maxLimit);
            
            if (isFirstCall) {
              logQueryTime(`subscribeToProductsByCategory[${category}] (fallback)`, startTime, filtered.length, allSnapshot.metadata.fromCache);
              if (filtered.length > 0) {
                console.log(`✅ Found ${filtered.length} products using fallback filtering. Product categories:`, 
                  [...new Set(allProducts.map(p => p.category).filter(Boolean))]);
              } else {
                console.warn(`⚠️ No products matched category "${normalizedCategory}" even with fallback. Available categories:`, 
                  [...new Set(allProducts.map(p => p.category).filter(Boolean))]);
              }
              isFirstCall = false;
            }
            
            callback(filtered);
          },
          (fallbackError) => {
            console.error(`❌ Error in fallback category products subscription [${category}]:`, fallbackError);
            callback([]);
          }
        );
        
        return; // Don't process the empty result from the direct query
      }
      
      if (isFirstCall) {
        logQueryTime(`subscribeToProductsByCategory[${category}]`, startTime, products.length, fromCache);
        isFirstCall = false;
      }
      
      callback(products);
    },
    (error) => {
      // If index error, try fallback
      if (error.code === 'failed-precondition' && !useFallback) {
        console.warn(`⚠️ Missing Firestore index for category query. Using fallback: fetching all products and filtering client-side.`);
        useFallback = true;
        
        // OPTIMIZED: Reduced limit from 3x to 1.5x to improve performance
        const fallbackLimit = Math.min(Math.ceil(maxLimit * 1.5), 750); // Cap at 750 products
        const allProductsQuery = query(productsRef, limit(fallbackLimit));
        
        // OPTIMIZED: Add timeout to fallback
        const fallbackTimeout = setTimeout(() => {
          if (fallbackUnsubscribe) {
            fallbackUnsubscribe();
            fallbackUnsubscribe = null;
          }
          console.warn(`⚠️ Fallback query timeout for category "${normalizedCategory}"`);
          callback([]);
        }, 2000); // 2 second timeout
        
        fallbackUnsubscribe = onSnapshot(
          allProductsQuery,
          (allSnapshot) => {
            clearTimeout(fallbackTimeout);
            
            const allProducts = allSnapshot.docs.map((doc) => {
              const data = doc.data();
              const { id: customId, ...dataWithoutId } = data;
              return { 
                id: doc.id,
                ...dataWithoutId,
                customId: customId || null
              };
            });
            
            // OPTIMIZED: More efficient filtering - pre-compute normalized category once
            const normalizedSearchCatNoSpaces = normalizedCategory.replace(/[\s-]/g, '');
            
            // Filter products by category (case-insensitive, handle variations)
            const filtered = allProducts.filter(product => {
              const productCategory = String(product.category || '').toLowerCase().trim();
              // Try exact match first (fastest)
              if (productCategory === normalizedCategory) return true;
              // Try matching without spaces/dashes
              const normalizedProductCat = productCategory.replace(/[\s-]/g, '');
              if (normalizedProductCat === normalizedSearchCatNoSpaces) return true;
              // Try partial match
              if (productCategory.includes(normalizedCategory) || normalizedCategory.includes(productCategory)) {
                return true;
              }
              return false;
            }).slice(0, maxLimit);
            
            callback(filtered);
          },
          (fallbackError) => {
            clearTimeout(fallbackTimeout);
            console.error(`❌ Error in fallback category products subscription [${category}]:`, fallbackError);
            callback([]);
          }
        );
      } else {
        console.error(`❌ Error in category products subscription [${category}]:`, error);
        callback([]);
      }
    }
  );
  
  // Return cleanup function that unsubscribes from both queries
  return () => {
    if (unsubscribe) unsubscribe();
    if (fallbackUnsubscribe) fallbackUnsubscribe();
  };
};

/**
 * Real-time subscription for categories
 * Uses indexed fields: isActive, displayOrder (ensure composite index)
 * Falls back to client-side filtering if index is missing
 * Returns unsubscribe function for cleanup
 */
export const subscribeToCategories = (callback, maxLimit = MAX_CATEGORIES_LIMIT) => {
  if (!isFirebaseInitialized() || !db) {
    callback([]);
    return () => {};
  }
  
  const startTime = Date.now();
  const categoriesRef = collection(db, CATEGORIES_COLLECTION);
  
  let isFirstCall = true;
  let hasCalledBack = false;
  let unsubscribe = null;
  let fallbackUnsubscribe = null;
  let useFallback = false;
  
  // CRITICAL: Reduced timeout from 300ms to 100ms for faster first paint
  // Timeout to ensure callback is called quickly for new users
  const timeoutId = setTimeout(() => {
    if (!hasCalledBack) {
      // Call with empty array immediately so UI can render
      callback([]);
      hasCalledBack = true;
    }
  }, 100); // Reduced from 300ms to 100ms for faster first paint
  
  // Try optimized query first (with index)
  const categoriesQuery = query(
    categoriesRef,
    where('isActive', '==', true),
    orderBy('displayOrder', 'asc'),
    limit(maxLimit)
  );
  
  unsubscribe = onSnapshot(
    categoriesQuery,
    (snapshot) => {
      clearTimeout(timeoutId);
      hasCalledBack = true;
      const fromCache = snapshot.metadata.fromCache;
      const categories = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (isFirstCall) {
        logQueryTime('subscribeToCategories', startTime, categories.length, fromCache);
        isFirstCall = false;
      }
      
      callback(categories);
    },
    (error) => {
      // CRITICAL: Ignore harmless WebChannel connection errors (404s during connection establishment)
      // These are expected and don't affect functionality - Firestore will retry automatically
      const errorMessage = error.message || '';
      const isWebChannelError = errorMessage.includes('Listen/channel') || 
                                errorMessage.includes('404') ||
                                error.code === 'unavailable';
      
      // If index is missing, fall back to fetching all and filtering client-side
      if (error.code === 'failed-precondition' && !useFallback) {
        console.warn('⚠️ Missing Firestore index! Falling back to client-side filtering. Create composite index for: categories (isActive, displayOrder)');
        useFallback = true;
        
        // Fallback: Fetch all categories and filter client-side
        const fallbackQuery = query(
          categoriesRef,
          limit(maxLimit * 2) // Get more to account for filtering
        );
        
        fallbackUnsubscribe = onSnapshot(
          fallbackQuery,
          (snapshot) => {
            clearTimeout(timeoutId);
            hasCalledBack = true;
            const fromCache = snapshot.metadata.fromCache;
            
            // Filter and sort client-side
            const allCategories = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data()
            }));
            
            const categories = allCategories
              .filter(cat => cat.isActive === true)
              .sort((a, b) => {
                const orderA = a.displayOrder ?? 999;
                const orderB = b.displayOrder ?? 999;
                return orderA - orderB;
              })
              .slice(0, maxLimit);
            
            if (isFirstCall) {
              logQueryTime('subscribeToCategories (fallback)', startTime, categories.length, fromCache);
              isFirstCall = false;
            }
            
            callback(categories);
          },
          (fallbackError) => {
            clearTimeout(timeoutId);
            hasCalledBack = true;
            
            // Ignore WebChannel connection errors
            const isFallbackWebChannelError = (fallbackError.message || '').includes('Listen/channel') ||
                                             (fallbackError.message || '').includes('404') ||
                                             fallbackError.code === 'unavailable';
            
            if (!isFallbackWebChannelError && fallbackError.code !== 'permission-denied') {
              console.warn("⚠️ Error in categories fallback subscription:", fallbackError.code || fallbackError.message);
            }
            callback([]);
          }
        );
      } else {
        clearTimeout(timeoutId);
        hasCalledBack = true;
        
        // Only log unexpected errors (not WebChannel connection errors)
        if (!isWebChannelError) {
          if (error.code === 'failed-precondition') {
            console.warn('⚠️ Missing Firestore index! Create composite index for: categories (isActive, displayOrder)');
          } else if (error.code !== 'permission-denied') {
            console.warn("⚠️ Error in categories subscription:", error.code || error.message);
          }
        }
        callback([]);
      }
    }
  );
  
  return () => {
    clearTimeout(timeoutId);
    if (unsubscribe) unsubscribe();
    if (fallbackUnsubscribe) fallbackUnsubscribe();
  };
};

/**
 * Real-time subscription for coupons
 * Uses indexed fields: isActive
 * Returns unsubscribe function for cleanup
 */
export const subscribeToCoupons = (callback, maxLimit = MAX_COUPONS_LIMIT) => {
  if (!isFirebaseInitialized() || !db) {
    callback({});
    return () => {};
  }
  
  const startTime = Date.now();
  const couponsRef = collection(db, COUPONS_COLLECTION);
  
  // Query uses indexed field: isActive
  // Ensure Firestore has index: coupons: isActive (ASC)
  const couponsQuery = query(
    couponsRef, 
    where('isActive', '==', true),
    limit(maxLimit)
  );
  
  let isFirstCall = true;
  
  const unsubscribe = onSnapshot(
    couponsQuery,
    (snapshot) => {
      const fromCache = snapshot.metadata.fromCache;
      const coupons = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        coupons[data.code] = { id: doc.id, ...data };
      });
      
      if (isFirstCall) {
        logQueryTime('subscribeToCoupons', startTime, Object.keys(coupons).length, fromCache);
        isFirstCall = false;
      }
      
      callback(coupons);
    },
    (error) => {
      console.error("❌ Error in coupons subscription:", error);
      if (error.code === 'failed-precondition') {
        console.error('⚠️ Missing Firestore index! Create index for: coupons (isActive)');
      }
      callback({});
    }
  );
  
  return unsubscribe;
};

/**
 * Real-time subscription for active announcements (optimized for AnnouncementBar)
 * Uses indexed fields: isActive
 * Returns unsubscribe function for cleanup
 * Filters by date range and sorts by priority
 */
export const subscribeToActiveAnnouncements = (callback, maxLimit = MAX_ANNOUNCEMENTS_LIMIT) => {
  if (!isFirebaseInitialized() || !db) {
    callback([]);
    return () => {};
  }
  
  const startTime = Date.now();
  const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
  
  // OPTIMIZED: Add orderBy to use existing composite index: isActive + createdAt
  // This makes the query much faster and uses the index efficiently
  // Index exists: announcements (isActive ASC, createdAt DESC)
  const announcementsQuery = query(
    announcementsRef,
    where('isActive', '==', true),
    orderBy('createdAt', 'desc'), // Use index for faster queries
    limit(maxLimit)
  );
  
  let isFirstCall = true;
  let hasCalledBack = false;
  let cachedDataReturned = false;
  
  // CRITICAL: Try to get from cache first for instant loading
  // This provides immediate data for returning users
  const tryCacheFirst = async () => {
    try {
      const cacheSnapshot = await getDocsFromCache(announcementsQuery).catch(() => null);
      
      if (cacheSnapshot && cacheSnapshot.docs.length > 0) {
        const now = new Date();
        const announcements = cacheSnapshot.docs
          .map(doc => {
            const data = doc.data();
            return { id: doc.id, ...data };
          })
          .filter(announcement => {
            // Filter by date range
            let startDate = null;
            let endDate = null;
            
            if (announcement.startDate) {
              startDate = announcement.startDate?.toDate?.() || new Date(announcement.startDate || 0);
            }
            if (announcement.endDate) {
              endDate = announcement.endDate?.toDate?.() || new Date(announcement.endDate || 9999999999999);
            }
            
            if (!startDate && !endDate) return true;
            if (startDate && now < startDate) return false;
            if (endDate && now > endDate) return false;
            return true;
          })
          .sort((a, b) => {
            if (a.priority !== b.priority) {
              return (b.priority || 1) - (a.priority || 1);
            }
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return dateB - dateA;
          });
        
        if (announcements.length > 0) {
          cachedDataReturned = true;
          logQueryTime('subscribeToActiveAnnouncements', startTime, announcements.length, true);
          callback(announcements);
          hasCalledBack = true;
        }
      }
    } catch (err) {
      // Cache not available or query not cached yet - continue with onSnapshot
    }
  };
  
  // Try cache first (non-blocking)
  tryCacheFirst();
  
  // CRITICAL: Reduced timeout from 500ms to 100ms for faster first paint
  const timeoutId = setTimeout(() => {
    if (!hasCalledBack) {
      callback([]);
      hasCalledBack = true;
    }
  }, 100);
  
  // onSnapshot automatically uses cache first, then syncs with server
  // This provides instant loading for returning users
  const unsubscribe = onSnapshot(
    announcementsQuery,
    (snapshot) => {
      clearTimeout(timeoutId);
      hasCalledBack = true;
      
      const fromCache = snapshot.metadata.fromCache;
      const now = new Date();
      
      // Process announcements with date filtering and sorting
      const announcements = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return { id: doc.id, ...data };
        })
        .filter(announcement => {
          // Filter by date range
          let startDate = null;
          let endDate = null;
          
          if (announcement.startDate) {
            startDate = announcement.startDate?.toDate?.() || new Date(announcement.startDate || 0);
          }
          if (announcement.endDate) {
            endDate = announcement.endDate?.toDate?.() || new Date(announcement.endDate || 9999999999999);
          }
          
          // If no dates specified, show announcement
          if (!startDate && !endDate) return true;
          
          // Check if current date is within range
          if (startDate && now < startDate) return false;
          if (endDate && now > endDate) return false;
          
          return true;
        })
        .sort((a, b) => {
          // Sort by priority first (higher priority first)
          if (a.priority !== b.priority) {
            return (b.priority || 1) - (a.priority || 1);
          }
          // Then by creation date (newer first)
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return dateB - dateA;
        });
      
      // Only log if this is the first call and we didn't already return cached data
      if (isFirstCall && !cachedDataReturned) {
        logQueryTime('subscribeToActiveAnnouncements', startTime, announcements.length, fromCache);
        isFirstCall = false;
      } else if (isFirstCall) {
        isFirstCall = false;
      }
      
      // Only update if we have new data or this is from server (not duplicate cache call)
      if (!fromCache || !cachedDataReturned) {
        callback(announcements);
      }
    },
    (error) => {
      clearTimeout(timeoutId);
      hasCalledBack = true;
      
      // Only log unexpected errors
      if (error.code !== 'permission-denied' && error.code !== 'unavailable') {
        console.warn("⚠️ Error in active announcements subscription:", error.code || error.message);
      }
      if (error.code === 'failed-precondition') {
        console.error('⚠️ Missing Firestore index! Create index for: announcements (isActive, createdAt)');
      }
      if (!cachedDataReturned) {
        callback([]);
      }
    }
  );
  
  return () => {
    clearTimeout(timeoutId);
    unsubscribe();
  };
};

/**
 * Real-time subscription for announcements (all announcements)
 * Uses indexed fields: isActive
 * Returns unsubscribe function for cleanup
 */
export const subscribeToAnnouncements = (callback, maxLimit = MAX_ANNOUNCEMENTS_LIMIT) => {
  if (!isFirebaseInitialized() || !db) {
    callback([]);
    return () => {};
  }
  
  const startTime = Date.now();
  const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
  
  // Query uses indexed field: isActive
  // Ensure Firestore has index: announcements: isActive (ASC)
  const announcementsQuery = query(
    announcementsRef,
    where('isActive', '==', true),
    limit(maxLimit)
  );
  
  let isFirstCall = true;
  
  const unsubscribe = onSnapshot(
    announcementsQuery,
    (snapshot) => {
      const fromCache = snapshot.metadata.fromCache;
      const now = new Date();
      const announcements = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(announcement => {
          const startDate = announcement.startDate?.toDate?.() || new Date(announcement.startDate || 0);
          const endDate = announcement.endDate?.toDate?.() || new Date(announcement.endDate || 9999999999999);
          return (!announcement.startDate || now >= startDate) && 
                 (!announcement.endDate || now <= endDate);
        })
        .sort((a, b) => (b.priority || 1) - (a.priority || 1));
      
      if (isFirstCall) {
        logQueryTime('subscribeToAnnouncements', startTime, announcements.length, fromCache);
        isFirstCall = false;
      }
      
      callback(announcements);
    },
    (error) => {
      console.error("❌ Error in announcements subscription:", error);
      if (error.code === 'failed-precondition') {
        console.error('⚠️ Missing Firestore index! Create index for: announcements (isActive)');
      }
      callback([]);
    }
  );
  
  return unsubscribe;
};

/**
 * Real-time subscription for founder videos
 * Uses indexed fields: order
 * Returns unsubscribe function for cleanup
 */
export const subscribeToFounderVideos = (callback, maxLimit = MAX_VIDEOS_LIMIT) => {
  if (!isFirebaseInitialized() || !db) {
    callback([]);
    return () => {};
  }
  
  const startTime = Date.now();
  const videosRef = collection(db, FOUNDER_VIDEOS_COLLECTION);
  
  // Query uses indexed field: order
  // Ensure Firestore has index: founderVideos: order (ASC)
  const videosQuery = query(
    videosRef, 
    orderBy("order", "asc"),
    limit(maxLimit)
  );
  
  let isFirstCall = true;
  
  const unsubscribe = onSnapshot(
    videosQuery,
    (snapshot) => {
      const fromCache = snapshot.metadata.fromCache;
      const videos = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      
      if (isFirstCall) {
        logQueryTime('subscribeToFounderVideos', startTime, videos.length, fromCache);
        isFirstCall = false;
      }
      
      callback(videos);
    },
    (error) => {
      console.error("❌ Error in founder videos subscription:", error);
      if (error.code === 'failed-precondition') {
        console.error('⚠️ Missing Firestore index! Create index for: founderVideos (order)');
      }
      callback([]);
    }
  );
  
  return unsubscribe;
};

/**
 * Get single product by ID (uses cache-first with getDoc)
 * For one-time reads, getDoc is more efficient than onSnapshot
 */
export const getProductById = async (productId) => {
  if (!isFirebaseInitialized() || !db || !productId) {
    return null;
  }
  
  const startTime = Date.now();
  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  
  try {
    // getDoc automatically uses cache first, then server
    const docSnapshot = await getDoc(productRef);
    
    if (!docSnapshot.exists()) {
      return null;
    }
    
    const data = docSnapshot.data();
    const fromCache = docSnapshot.metadata.fromCache;
    logQueryTime(`getProductById[${productId}]`, startTime, 1, fromCache);
    
    const { id: customId, ...dataWithoutId } = data;
    return {
      id: docSnapshot.id,
      ...dataWithoutId,
      customId: customId || null
    };
  } catch (error) {
    console.error(`❌ Error fetching product ${productId}:`, error);
    return null;
  }
};

